/**
 * ConsentPrompt component.
 *
 * Renders a PDPA consent card with configurable text, a Terms of Use link,
 * and "I agree" / "Decline" action buttons. Displayed as a distinct system
 * card in the message list before any personal data collection.
 *
 * The backend triggers this by sending a message with type "consent_request".
 * Consent state is tracked per session (not persisted across page reloads).
 */

import type {
  ResolvedWidgetConfig,
  ConsentStatus,
  ConsentEvent,
  ConsentCallbacks,
} from "../types";

export interface ConsentPromptOptions {
  config: ResolvedWidgetConfig;
  callbacks: ConsentCallbacks;
  /** Override the consent message (e.g., from backend consent_request) */
  message?: string;
  /** Override the TOU URL (e.g., from backend consent_request) */
  touUrl?: string;
}

export class ConsentPrompt {
  private readonly element: HTMLElement;
  private readonly agreeBtn: HTMLButtonElement;
  private readonly declineBtn: HTMLButtonElement;
  private readonly actionsEl: HTMLElement;
  private status: ConsentStatus = "pending";

  constructor(private readonly options: ConsentPromptOptions) {
    const { config, callbacks } = options;
    const message = options.message ?? config.consentMessage;
    const touUrl = options.touUrl ?? config.touUrl;

    this.element = document.createElement("div");
    this.element.className = "hana-consent";
    this.element.setAttribute("role", "alert");
    this.element.setAttribute("aria-label", "Data collection consent request");

    // Consent text paragraph
    const textEl = document.createElement("p");
    textEl.className = "hana-consent__text";
    textEl.textContent = message;
    this.element.appendChild(textEl);

    // TOU link
    const touLink = document.createElement("a");
    touLink.className = "hana-consent__tou-link";
    touLink.href = touUrl;
    touLink.target = "_blank";
    touLink.rel = "noopener noreferrer";
    touLink.textContent = "Terms of Use";
    touLink.setAttribute("aria-label", "View Terms of Use (opens in new tab)");
    this.element.appendChild(touLink);

    // Action buttons container
    this.actionsEl = document.createElement("div");
    this.actionsEl.className = "hana-consent__actions";

    // "I agree" button
    this.agreeBtn = document.createElement("button");
    this.agreeBtn.className = "hana-consent__btn hana-consent__btn--agree";
    this.agreeBtn.setAttribute("type", "button");
    this.agreeBtn.setAttribute("aria-label", "I agree to data collection");
    this.agreeBtn.textContent = "I agree";
    this.agreeBtn.addEventListener("click", () => {
      this.handleResponse(true, callbacks);
    });

    // "Decline" button
    this.declineBtn = document.createElement("button");
    this.declineBtn.className = "hana-consent__btn hana-consent__btn--decline";
    this.declineBtn.setAttribute("type", "button");
    this.declineBtn.setAttribute("aria-label", "Decline data collection");
    this.declineBtn.textContent = "Decline";
    this.declineBtn.addEventListener("click", () => {
      this.handleResponse(false, callbacks);
    });

    this.actionsEl.appendChild(this.agreeBtn);
    this.actionsEl.appendChild(this.declineBtn);
    this.element.appendChild(this.actionsEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getStatus(): ConsentStatus {
    return this.status;
  }

  private handleResponse(
    consentGiven: boolean,
    callbacks: ConsentCallbacks,
  ): void {
    if (this.status !== "pending") return;

    this.status = consentGiven ? "granted" : "declined";

    const event: ConsentEvent = {
      timestamp: new Date().toISOString(),
      entityId: this.options.config.entityId,
      consentGiven,
    };

    // Disable buttons to prevent double-clicks
    this.agreeBtn.disabled = true;
    this.declineBtn.disabled = true;

    // Replace buttons with status message
    const statusEl = document.createElement("div");
    statusEl.className = consentGiven
      ? "hana-consent__status hana-consent__status--granted"
      : "hana-consent__status hana-consent__status--declined";
    statusEl.textContent = consentGiven
      ? "Consent granted. Thank you."
      : "Data collection declined. You can still ask general questions.";
    statusEl.setAttribute("role", "status");

    // Remove action buttons and add status
    this.actionsEl.remove();
    this.element.appendChild(statusEl);

    // Emit the appropriate callback
    if (consentGiven) {
      callbacks.onConsent(event);
    } else {
      callbacks.onDecline(event);
    }
  }
}
